import { apiFetch } from "@/lib/api";
import type {
  AssetsListAsset,
  AssetsListDetail,
  AssetsListEntry,
  AssetsListSummary,
} from "@kagami/shared/assetsList";

import type {
  AssetsListSnapshot,
  CreateAssetsListOptions,
  DeleteAssetsListOptions,
  FetchAssetByIdOptions,
  FetchAssetsListAssetsOptions,
  FetchAssetsListOptions,
  FetchAssetsListsOptions,
  UpdateAssetsListOptions,
  WorkingAssetsListEntry,
} from "./types";

const assetsListsPath = (showId: string) => `/api/shows/${showId}/assets-lists`;

export class AssetsListApiError extends Error {
  readonly status: number;

  constructor({ message, status }: { message: string; status: number }) {
    super(message);
    this.name = "AssetsListApiError";
    this.status = status;
  }
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body.error === "string") return body.error;
  } catch {}
  return response.statusText || `HTTP ${response.status}`;
}

/**
 * @input {_id: 'l1', assets: [{asset_id: {_id: 'a1'}, in_time: null, out_time: null}]}
 * @output {assetsListId: 'l1', entries: [{entryKey: '…', asset_id: 'a1', …}], assets: [{_id: 'a1'}]}
 */
function toSnapshot(detail: AssetsListDetail): AssetsListSnapshot {
  if (!detail || typeof detail !== "object") {
    throw new AssetsListApiError({
      message: `Assets list response was not an object (got ${typeof detail})`,
      status: 500,
    });
  }
  if (!detail._id) {
    throw new AssetsListApiError({
      message: `Assets list response is missing _id (name: ${detail.name ?? "unknown"})`,
      status: 500,
    });
  }

  const populatedEntries = Array.isArray(detail.assets) ? detail.assets : [];

  const entries: WorkingAssetsListEntry[] = [];
  const assets: AssetsListAsset[] = [];

  for (const populatedEntry of populatedEntries) {
    const populatedAsset = populatedEntry.asset_id;

    // A hard-deleted asset comes back unpopulated or null.
    if (!populatedAsset || !populatedAsset._id) continue;

    entries.push({
      entryKey: crypto.randomUUID(),
      asset_id: populatedAsset._id,
      in_time: populatedEntry.in_time ?? null,
      out_time: populatedEntry.out_time ?? null,
      source_frame_rate: populatedEntry.source_frame_rate ?? null,
      source_duration: populatedEntry.source_duration ?? null,
      source_width: populatedEntry.source_width ?? null,
      source_height: populatedEntry.source_height ?? null,
    });
    assets.push(populatedAsset);
  }

  return {
    assetsListId: detail._id,
    name: detail.name,
    visibility: detail.visibility,
    ownerUserId: detail.user ? detail.user.id : null,
    entries,
    assets,
  };
}

function toWireEntries(entries: WorkingAssetsListEntry[]): AssetsListEntry[] {
  return entries.map((entry) => ({
    asset_id: entry.asset_id,
    in_time: entry.in_time,
    out_time: entry.out_time,
    source_frame_rate: entry.source_frame_rate ?? null,
    source_duration: entry.source_duration ?? null,
    source_width: entry.source_width ?? null,
    source_height: entry.source_height ?? null,
  }));
}

/**
 * @input {showId: 's1'}
 * @output [{_id: 'l1', name: 'Opening sequence', assetCount: 4, …}]
 */
export async function fetchAssetsLists({
  showId,
}: FetchAssetsListsOptions): Promise<AssetsListSummary[]> {
  const response = await apiFetch(assetsListsPath(showId));

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to load assets lists for show ${showId}: ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }

  const body = await response.json();
  if (!Array.isArray(body)) return [];
  return body;
}

/**
 * @input {showId: 's1', assetsListId: 'l1'}
 * @output {assetsListId: 'l1', name: 'Opening sequence', entries: [...], assets: [...]}
 */
export async function fetchAssetsList({
  showId,
  assetsListId,
}: FetchAssetsListOptions): Promise<AssetsListSnapshot> {
  const response = await apiFetch(`${assetsListsPath(showId)}/${assetsListId}`);

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to load assets list ${assetsListId}: ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }

  return toSnapshot(await response.json());
}

/**
 * @input {showId: 's1', name: 'Opening sequence', visibility: 'private', entries: [...]}
 * @output {assetsListId: 'l9', name: 'Opening sequence', entries: [...], assets: [...]}
 */
export async function createAssetsList({
  showId,
  name,
  visibility,
  entries,
}: CreateAssetsListOptions): Promise<AssetsListSnapshot> {
  const response = await apiFetch(assetsListsPath(showId), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name, visibility, assets: toWireEntries(entries) }),
  });

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to create assets list "${name}": ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }

  return toSnapshot(await response.json());
}

/**
 * The api answers 404 for "not yours" as well as "no such list"; Save relies on
 * that to fall through to Save As.
 *
 * @input {showId: 's1', assetsListId: 'l1', name: 'Opening sequence', visibility: 'private', entries: [...]}
 * @output {assetsListId: 'l1', name: 'Opening sequence', entries: [...], assets: [...]}
 */
export async function updateAssetsList({
  showId,
  assetsListId,
  name,
  visibility,
  entries,
}: UpdateAssetsListOptions): Promise<AssetsListSnapshot> {
  const response = await apiFetch(
    `${assetsListsPath(showId)}/${assetsListId}`,
    {
      method: "PUT",
      body: JSON.stringify({
        name,
        visibility,
        assets: toWireEntries(entries),
      }),
    },
  );

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to save assets list ${assetsListId}: ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }

  return toSnapshot(await response.json());
}

/**
 * Owner-only: 404 covers "not yours" as well as "no such list".
 *
 * @input {showId: 's1', assetsListId: 'l1'}
 * @output undefined
 */
export async function deleteAssetsList({
  showId,
  assetsListId,
}: DeleteAssetsListOptions): Promise<void> {
  const response = await apiFetch(
    `${assetsListsPath(showId)}/${assetsListId}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to delete assets list ${assetsListId}: ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }
}

/**
 * The assets grid only ever holds unarchived assets, so an archived entry has
 * no fresher copy to resolve against. This route carries no archived filter
 * and is the only way to learn an entry's current state.
 *
 * @input {showId: 's1', assetId: 'a1'}
 * @output {_id: 'a1', name: 'Goal', archived: true, …}
 */
export async function fetchAssetById({
  showId,
  assetId,
}: FetchAssetByIdOptions): Promise<AssetsListAsset> {
  const response = await apiFetch(`/api/shows/${showId}/assets/${assetId}`);

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to load asset ${assetId}: ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }

  return response.json();
}

/**
 * @input {showId: 's1', assetsListId: 'l1'}
 * @output [{_id: 'a1', name: 'Goal', mediaType: 'video', archived: true, …}]
 */
export async function fetchAssetsListAssets({
  showId,
  assetsListId,
  assetIds,
}: FetchAssetsListAssetsOptions): Promise<AssetsListAsset[]> {
  // A list with unsaved changes names its own entries: the saved list either
  // doesn't exist yet or no longer matches what the strip is showing.
  const query = assetIds?.length
    ? `?${new URLSearchParams({ ids: assetIds.join(",") })}`
    : "";
  const response = await apiFetch(
    `${assetsListsPath(showId)}/${assetsListId ?? ""}/assets${query}`,
  );

  if (!response.ok) {
    throw new AssetsListApiError({
      message: `Failed to load assets for list ${assetsListId ?? "(unsaved)"}: ${await readErrorMessage(response)}`,
      status: response.status,
    });
  }

  const body = await response.json();
  return Array.isArray(body) ? body : [];
}
